import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { Observable } from "rxjs";
import { apiColletions, quickCommerceApiColletions } from "src/app/core/constants/api_collection";

@Injectable({
  providedIn: "root"
})
export class BookingService {
  constructor(private apiService: ApiService) {}

  getCustomerDetails(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.getCustomerDetails, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getVehicleTypeList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.getVehicleTypes, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getNearestDriver(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.getNearestDriver, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  validatePromoCode(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.validatePromoCode, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  getPromoCodeList(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.promoCodeList, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  createRideBooking(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.createBooking, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            // The readable booking number comes back in the body (browsers drop
            // the `identifier` header unless CORS exposes it); `id` is the Mongo
            // _id used for socket tracking.
            observer.next({
              data: response.data,
              id: response.id,
              identifier: response.data?.data?.uniqueId ?? response.identifier,
              isPaymentRequired: !!(response.isPaymentRequired || response.data?.isPaymentRequired || response.data?.isCapturePaymentPending)
            });
            observer.complete();
          } else {
            observer.next({ failed: true, message: response?.message || "" });
            observer.complete();
          }
        },
        error: (err: any) => {
          observer.next({ failed: true, message: err?.error?.message || err?.message || "" });
          observer.complete();
        }
      });
    });
  }

  /**
   * Spec — Recurring booking. POSTs the rule to `/api/booking/recurrence`.
   * The server persists the rule and a cron spawns individual SCHEDULE
   * bookings on each matching day. Returns the recurrence id on success.
   */
  createBookingRecurrence(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.bookingRecurrence, parameters }).subscribe({
        next: (response) => {
          observer.next({ id: response?.id, data: response?.data });
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Recurring booking — management endpoints.
  //
  // All scoped server-side by the caller's auth token:
  //   - Dispatcher sees only rules whose cityId is in their cityIds[].
  //   - Customer sees only their own rules.
  //   - Admin sees everything.
  // PATCH is dispatcher/admin only; spawn-now is dispatcher/admin only.
  // ─────────────────────────────────────────────────────────────────────

  /** GET /api/booking/recurrence — paginated, optional status/customer filter. */
  listBookingRecurrences(parameters: { status?: number; page?: number; limit?: number; customerId?: string; cityId?: string }): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: apiColletions.bookingRecurrence, parameters }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data || { recurrences: [], dataCount: 0, page: 1, limit: 20 });
          observer.complete();
        },
        error: () => {
          observer.next({ recurrences: [], dataCount: 0, page: 1, limit: 20 });
          observer.complete();
        }
      });
    });
  }

  /** GET /api/booking/recurrence/:id — single rule. */
  getBookingRecurrence(id: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: `${apiColletions.bookingRecurrence}/${id}`, parameters: {} }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.recurrence || null);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  /** PUT /api/booking/recurrence/:id/pause — stop future spawning (past stays). */
  pauseBookingRecurrence(id: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.put({ url: `${apiColletions.bookingRecurrence}/${id}/pause`, parameters: {} }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.recurrence || null);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  /** PUT /api/booking/recurrence/:id/resume — resume spawning. */
  resumeBookingRecurrence(id: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.put({ url: `${apiColletions.bookingRecurrence}/${id}/resume`, parameters: {} }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.recurrence || null);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  /** PUT /api/booking/recurrence/:id/end — final stop, cancels future-spawned children. */
  endBookingRecurrence(id: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.put({ url: `${apiColletions.bookingRecurrence}/${id}/end`, parameters: {} }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.recurrence || true);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  /** PATCH /api/booking/recurrence/:id — edit days/time/endDate/addresses/note. */
  updateBookingRecurrence(id: string, parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.patch({ url: `${apiColletions.bookingRecurrence}/${id}`, parameters }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.recurrence || null);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  /** POST /api/booking/recurrence/:id/spawn-now — manual one-shot spawn for testing. */
  spawnNowRecurrence(id: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: `${apiColletions.bookingRecurrence}/${id}/spawn-now`, parameters: {} }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data || null);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  // Spec #19 — Booking Templates. Pass `null` for customerId to fetch only
  // global (country-scoped) templates.
  listBookingTemplates(customerId: string | null): Observable<any> {
    const parameters: any = { includeGlobal: "true" };
    if (customerId) parameters.customerId = customerId;
    return new Observable<any>((observer) => {
      this.apiService.get({ url: apiColletions.bookingTemplates, parameters }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.templates || []);
          observer.complete();
        },
        error: () => {
          observer.next([]);
          observer.complete();
        }
      });
    });
  }

  saveBookingTemplate(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.bookingTemplates, parameters }).subscribe({
        next: (response) => {
          observer.next(response?.data?.data?.templateId || null);
          observer.complete();
        },
        error: () => {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  deleteBookingTemplate(templateId: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.delete({ url: `${apiColletions.bookingTemplates}/${templateId}`, parameters: {} }).subscribe({
        next: () => { observer.next(true); observer.complete(); },
        error: () => { observer.next(false); observer.complete(); }
      });
    });
  }

  /**
   * Spec #1 — fetch incoming-call events the dispatcher missed while their
   * socket was reconnecting. Returns the array (or empty on error).
   */
  listMissedIncomingCalls(sinceMs: number): Observable<any[]> {
    return new Observable<any[]>((observer) => {
      this.apiService
        .get({
          url: "/api/dispatcher/incoming_call/recent",
          parameters: { since: String(sinceMs), isShowErrorToast: false }
        })
        .subscribe({
          next: (response) => {
            observer.next(response?.data?.data?.calls || []);
            observer.complete();
          },
          error: () => {
            observer.next([]);
            observer.complete();
          }
        });
    });
  }

  /**
   * Spec #1 — driver-by-phone fallback for the incoming-call popup.
   * Returns the driver object (or null when not found).
   */
  searchDriverByPhone(parameters: { phone: string; countryPhoneCode?: string }): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService
        .get({
          url: "/api/dispatcher/search_driver",
          parameters: { ...parameters, isShowErrorToast: false }
        })
        .subscribe({
          next: (response) => {
            observer.next(response?.data?.data?.driver || null);
            observer.complete();
          },
          error: () => {
            observer.next(null);
            observer.complete();
          }
        });
    });
  }

  /**
   * Quick Booking — drivers that could take this trip, nearest ETA first.
   *
   * The server runs the same eligibility resolver auto-dispatch uses, so the
   * driver at the top of this list is the one the trip would go to anyway;
   * passing that driver's `_id` as `driverId` on createBooking just makes the
   * assignment explicit.
   *
   * Resolves to `[]` (never errors) so an unreachable maps provider or an empty
   * fleet only blanks the card instead of blocking the booking.
   */
  getNearbyDrivers(parameters: any): Observable<any[]> {
    return new Observable<any[]>((observer) => {
      this.apiService.post({ url: apiColletions.nearbyDrivers, parameters: { ...parameters, isShowErrorToast: false } }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.drivers || []);
          observer.complete();
        },
        error: () => { observer.next([]); observer.complete(); }
      });
    });
  }

  /**
   * Corrects the caller's name on their account, from Quick Booking.
   *
   * The endpoint accepts names only — a call-taking screen must not be able to
   * repoint an account's phone, email or status.
   */
  updateCustomerName(customerId: string, parameters: { firstName: string; lastName?: string }): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.patch({ url: `${apiColletions.dispatcherUpdateCustomer}/${customerId}`, parameters }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.customer || true);
          observer.complete();
        },
        error: () => { observer.next(null); observer.complete(); }
      });
    });
  }

  /** Spec #12 — save an address on behalf of a customer. */
  saveCustomerAddress(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.address, parameters }).subscribe({
        next: () => { observer.next(true); observer.complete(); },
        error: () => { observer.next(false); observer.complete(); }
      });
    });
  }

  /**
   * Spec #12 — look up the shared business directory by an incoming phone.
   * Returns null when the call isn't from a known business.
   */
  findBusinessAddressByPhone(parameters: { phone: string; countryPhoneCode?: string }): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: apiColletions.businessAddressByPhone, parameters }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.businessAddress || null);
          observer.complete();
        },
        error: () => { observer.next(null); observer.complete(); }
      });
    });
  }

  /**
   * Spec #12 — dispatcher adds a business directory entry. Idempotent on
   * (country, phone): the backend returns the existing row when the phone
   * is already known.
   */
  createBusinessAddress(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.businessAddressCreate, parameters }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.businessAddress || null);
          observer.complete();
        },
        error: () => { observer.next(null); observer.complete(); }
      });
    });
  }

  // ── Gap 5 — Message Center ──────────────────────────────────────────
  /** Fans out to the targeted drivers server-side; returns the created row. */
  sendDispatcherMessage(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.dispatcherMessages, parameters }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data ?? null);
          observer.complete();
        },
        error: () => { observer.next(null); observer.complete(); }
      });
    });
  }

  /** Sent log with delivered/read counts already summarised by the server. */
  listDispatcherMessages(parameters: any = {}): Observable<any[]> {
    return new Observable<any[]>((observer) => {
      this.apiService.get({ url: apiColletions.dispatcherMessages, parameters }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.messages || []);
          observer.complete();
        },
        error: () => { observer.next([]); observer.complete(); }
      });
    });
  }

  /** Per-driver breakdown — only fetched when a log row is expanded. */
  getDispatcherMessageReceipts(messageId: string): Observable<any[]> {
    return new Observable<any[]>((observer) => {
      this.apiService.get({ url: `${apiColletions.dispatcherMessages}/${messageId}/receipts`, parameters: { isShowLoader: false } }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.receipts || []);
          observer.complete();
        },
        error: () => { observer.next([]); observer.complete(); }
      });
    });
  }

  // ── Gap 8 — alert bell ──────────────────────────────────────────────
  listDispatcherAlerts(): Observable<any[]> {
    return new Observable<any[]>((observer) => {
      this.apiService.get({ url: apiColletions.dispatcherAlerts, parameters: { isShowLoader: false, isShowErrorToast: false } }).subscribe({
        next: (response: any) => {
          observer.next(response?.data?.data?.alerts || []);
          observer.complete();
        },
        error: () => { observer.next([]); observer.complete(); }
      });
    });
  }

  resolveDispatcherAlert(alertId: string): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.put({ url: `${apiColletions.dispatcherAlerts}/${alertId}/resolve`, parameters: {} }).subscribe({
        next: () => { observer.next(true); observer.complete(); },
        error: () => { observer.next(false); observer.complete(); }
      });
    });
  }

  getDispatcherBoookings(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.get({ url: apiColletions.dispatcherBookingsMap, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  reassignBooking(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.post({ url: apiColletions.reassignBooking, parameters: parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /** Fleet Broadcast — force re-offer a stuck booking to nearby drivers. */
  fleetBroadcast(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.post({ url: apiColletions.fleetBroadcast, parameters: parameters }).subscribe({
        next: (response) => {
          observer.next(!!response);
          observer.complete();
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  findPicker(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.findPicker}/${parameters?.merchantId}`;
      this.apiService.post({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  findServiceProviders(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${quickCommerceApiColletions.serviceProviders}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  findServiceMerchants(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${quickCommerceApiColletions.serviceMerchants}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  changeDeliveryBookingStatus(bookingId: string, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const changeBookingStatusUrl = `${quickCommerceApiColletions.changeDeliveryStatus}/${bookingId}`;
      this.apiService.put({ url: changeBookingStatusUrl, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  assignMerchants(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${quickCommerceApiColletions.assignMerchants}`;
      this.apiService.put({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  acceptMerchantBooking(bookingId: string, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const acceptBookingUrl = `${quickCommerceApiColletions.acceptMerchantBooking}/${bookingId}`;
      this.apiService.put({ url: acceptBookingUrl, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  reAssingDriver(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.reAssignDriver, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  driverAccept(bookingId: string, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${apiColletions.acceptBooking}/${bookingId}`;
      this.apiService.put({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  driverReject(bookingId: string, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${apiColletions.rejectBooking}/${bookingId}`;
      this.apiService.put({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  pickerAccept(bookingId: string, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${quickCommerceApiColletions.acceptPicker}/${bookingId}`;
      this.apiService.put({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }
  pickerReject(bookingId: string, parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      const url: string = `${quickCommerceApiColletions.rejectPicker}/${bookingId}`;
      this.apiService.put({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response) {
            observer.next(response);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  checkBussinessType(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.checkBusinessType, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  checkBusinessCategory(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.checkBusinessCategory, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }



  // Declared as `any` because it actually emits the questions ARRAY (or false on
  // failure) — the old `Observable<boolean>` matched neither branch.
  getQuestionsList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: apiColletions.question, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  submitBookingQuestions(bookingId: string, formData: FormData): Observable<any> {
    return new Observable<boolean>((observer) => {
      const updateAddressUrl = `${apiColletions.question}/bookingQuestions/${bookingId}`;
      this.apiService.put({ url: updateAddressUrl, parameters: formData }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }



}
